import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

export const formatDiscountUserDisplay = (user) => {
  if (!user) return '';
  const fullName = [user.surname, user.name].filter((x) => x && String(x).trim()).join(' ').trim();
  const parts = [user.id];
  if (fullName) parts.push(fullName);
  if (user.phoneNumber) parts.push(user.phoneNumber);
  if (user.emailAddress) parts.push(user.emailAddress);
  return parts.join(' · ');
};

/** Для імені/прізвища лишаємо пробіли; для цифрового вводу — нормалізація телефону/ID. */
const cleanSearchQuery = (value) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (/[\p{L}'’`]/u.test(trimmed)) {
    return trimmed.replace(/\s+/g, ' ');
  }
  let cleaned = trimmed.replace(/[\s()+\-]/g, '');
  if (cleaned.length <= 4 && /^\d+$/.test(cleaned)) {
    return cleaned;
  }
  if (cleaned.startsWith('38') && cleaned.length === 12 && /^38\d{10}$/.test(cleaned)) {
    cleaned = cleaned.substring(2);
  }
  return cleaned;
};

/**
 * Пошук клієнта для блоку знижки: debounce + dropdown через GET /api/v1/user?idOrPhoneOrEmail=
 * При виборі — onSelect(user); на бекенд apply-discount йде лише phoneNumber.
 */
const DiscountUserSearchInput = ({
  token,
  selectedUser,
  searchQuery,
  onSearchQueryChange,
  onSelect,
  onClear,
  disabled = false,
  placeholder = 'Клієнт (телефон, прізвище, email)',
  inputId,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  const displayValue = selectedUser ? formatDiscountUserDisplay(selectedUser) : (searchQuery || '');
  const hasValue = Boolean(selectedUser || (searchQuery && searchQuery.trim()));

  useEffect(() => () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  }, []);

  const loadUsers = (query) => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    axios
      .get('/api/v1/user?idOrPhoneOrEmail=' + encodeURIComponent(query.trim()), {
        headers: token ? { Authorization: token } : {},
      })
      .then((res) => {
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setResults(list);
      })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  };

  const handleChange = (e) => {
    if (disabled) return;
    const cleaned = cleanSearchQuery(e.target.value);
    onSearchQueryChange(cleaned);
    setShowDropdown(true);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (cleaned.trim().length < 2) {
      setResults([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(() => loadUsers(cleaned), 300);
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    onClear();
    setResults([]);
    setShowDropdown(false);
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <div className="position-relative w-100">
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        className="form-control"
        value={displayValue}
        onChange={handleChange}
        onFocus={() => {
          if (disabled || selectedUser) return;
          setShowDropdown(true);
          if ((searchQuery || '').trim().length >= 2 && results.length === 0) {
            loadUsers(searchQuery);
          }
        }}
        onBlur={() => setShowDropdown(false)}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={!!selectedUser}
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        name="discount-client-search"
        data-lpignore="true"
        data-form-type="other"
        data-1p-ignore="true"
        style={selectedUser ? { paddingRight: '2rem' } : undefined}
      />
      {hasValue && !disabled && (
        <span
          className="position-absolute"
          style={{
            right: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            cursor: 'pointer',
            fontSize: '1.2rem',
            color: '#6c757d',
            zIndex: 10,
          }}
          onMouseDown={handleClear}
          title="Очистити"
        >
          ×
        </span>
      )}
      {showDropdown && !selectedUser && !disabled && (
        <div
          className="position-absolute w-100 bg-white border rounded shadow-sm"
          style={{ zIndex: 1060, maxHeight: '260px', overflowY: 'auto', top: '100%', marginTop: 2 }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {loading ? (
            <div className="p-3 text-center text-muted">Завантаження...</div>
          ) : results.length > 0 ? (
            results.map((u) => (
              <div
                key={u.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(u);
                  setShowDropdown(false);
                  setResults([]);
                }}
                style={{ padding: 10, cursor: 'pointer', borderBottom: '1px solid #dee2e6' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
              >
                <div style={{ fontSize: '0.875rem' }}>{formatDiscountUserDisplay(u)}</div>
              </div>
            ))
          ) : (
            <div className="p-3 text-center text-muted">
              {(searchQuery || '').trim().length >= 2 ? 'Нічого не знайдено' : 'Введіть мін. 2 символи'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DiscountUserSearchInput;
